import { useState } from 'react';

import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import type { ReminderAction } from '@poseidon/components/ManualSelect';
import { cn } from '@poseidon/components/cn';
import { Button, Input, Label, ToggleSwitch } from '@poseidon/components/Input2';
import TipTapInput from '@poseidon/components/Input2/TipTapInput';
import { Kbd } from '@poseidon/components/Kbd';
import { ManualSelectPure } from '@poseidon/components/ManualSelect';
import { Alerts, Arrows, General } from '@poseidon/components/StrokeIcons';
import { getTextFromHtml } from '@poseidon/components/utils';

import type { SequenceAnyStepType, SequenceStep, StepIssue } from '~/components/Sequence/sequenceSteps';
import { macPC } from '@poseidon/components/hooks/useKey';
import { DEFAULT_LIKE_COUNT, STEP_META, stepEditSpec } from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The step being edited (controlled — the parent applies each change and passes it back). */
  step: SequenceStep;
  /** Live update as fields change (the parent commits locally and persists it — the builder as you type, a
   *  contact's copy when the editor closes). */
  onChange: (next: SequenceStep) => void;
  /** Save anything pending and close — the ✕, and ⌘/Ctrl+Enter in any field. Keep it stable: the text fields'
   *  ⌘/Ctrl+Enter binding captures it once. */
  onClose: () => void;
  /** `@`-variables (contact / user fields) offered in the body + subject editors. */
  variables?: TipTapVariable[];
  /** `/`-documents (the team's shareable docs) offered in the body + subject editors. */
  documents?: TipTapDocument[];
  /** The team's reusable reminder actions — the `reminder` step picks one from a dropdown (like campaigns). */
  reminderActions?: ReminderAction[];
  /** Whether the reminder-actions list is still loading. */
  reminderActionsLoading?: boolean;
  /** Create a new reminder action from the dropdown's "Create …" row. */
  onCreateReminder?: (name: string) => Promise<void>;
  /** Whether a reminder action is currently being created. */
  reminderCreating?: boolean;
  /**
   * Content-only mode (the per-contact locked graph): render ONLY the free-text fields a rep may override
   * for one contact — subject + body. The structural / non-text fields (`like` count, the `reminder` action
   * picker) are hidden, since a per-contact override tunes message text, not step configuration.
   */
  contentOnly?: boolean;
  /**
   * type: 'email' — the subject of the nearest earlier email step (null when this is the first email).
   * Non-null → show the "Continue previous email thread" toggle; when the toggle is on, this email
   * continues that thread, so the subject field is hidden and the prior subject is inherited.
   */
  previousEmailSubject?: string | null;
  /**
   * What's wrong with the step as it stands in the editor (the reason its node is ringed), or null. Shown
   * under the header so the rep sees WHY without hovering the node, and clears as soon as they fix it.
   */
  issue?: StepIssue | null;
}

/** Field copy per step type — body label + placeholders. */
const BODY_LABEL: Partial<Record<SequenceAnyStepType, string>> = {
  message: 'Message',
  connect: 'Connection request note (optional)',
  inmail: 'Body',
  email: 'Body',
  call: 'Note (optional)',
  reminder: 'Note (optional)',
};
const BODY_PLACEHOLDER: Partial<Record<SequenceAnyStepType, string>> = {
  message: 'Write the message…',
  connect: 'Add a note to the connection request…',
  inmail: 'Write the InMail…',
  email: 'Write the email…',
  call: 'What to cover on the call…',
  reminder: 'What to do…',
};

/**
 * Inline editor card for a sequence step's content — the body / subject / like-count fields the step
 * type exposes (see {@link stepEditSpec}). Body + subject use the same {@link TipTapInput} rich editor
 * as `/templates` (HTML in / out), with `@`-variables, `/`-documents, and `{`-comments enabled.
 * Controlled: every field change flows through `onChange`; the parent persists it (the builder as you type,
 * a contact's copy when the card closes). Renders nothing for a step type with no editable content.
 *
 * The mention dropdowns portal outside this card (as `.tippy-box` popups), so the parent's
 * click-outside-to-close ignores them (see {@link SequenceGraphEditor}) — picking a variable/document
 * must not dismiss the editor.
 */
export function SequenceStepEditor({
  step,
  onChange,
  onClose,
  variables,
  documents,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  contentOnly = false,
  previousEmailSubject = null,
  issue = null,
}: Props) {
  const spec = stepEditSpec(step.type);
  // The like count is a local string so the field can be cleared / retyped freely; the parsed, clamped
  // value is committed to the step on each valid change. Seeded once (the editor is keyed per step).
  const [likeStr, setLikeStr] = useState(String(step.likeCount ?? DEFAULT_LIKE_COUNT));
  if (spec == null) return null;

  // Email threading: an email that has an earlier email to reply to shows a "Continue previous email
  // thread" toggle (defaults ON). When on, this email continues that thread and inherits its subject —
  // the subject field is hidden. The toggle is template-level, so it's hidden in the per-contact
  // (contentOnly) override view; the inherited-subject state still hides the subject there.
  const showReplyToggle = step.type === 'email' && previousEmailSubject != null && !contentOnly;
  const threadContinued = step.type === 'email' && previousEmailSubject != null && (step.replyToPrevious ?? true);
  const inheritedSubjectText = previousEmailSubject != null ? getTextFromHtml(previousEmailSubject).trim() : '';

  const onLikeChange = (raw: string) => {
    setLikeStr(raw);
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) onChange({ ...step, likeCount: Math.max(1, Math.min(50, n)) });
  };

  // InMail / email are long-form — allow rich formatting + links, like the email step in /templates.
  const rich = step.type === 'inmail' || step.type === 'email';
  // The call note is a plain-text instruction the rep reads on the to-do — the engine flattens it to text
  // and does NOT run the template evaluator, so DON'T offer `@`-variables / `/`-documents / `{`-comments
  // here (they'd be stored as raw, unresolved tokens). Touch-step bodies keep them. (Reminder uses the
  // action dropdown above, not a note.)
  const isTaskNote = step.type === 'call';

  return (
    <div
      data-step-editor
      // Escape from inside a field saves and closes. (The shared esc stack the parent also registers on leaves
      // a keystroke in a text field alone — it covers Escape with focus elsewhere.) An Escape a mention list
      // used to close itself never gets here (the list stops it — `defaultPrevented` can't tell, ProseMirror
      // sets it on every Escape); and it stops here, so nothing behind the editor (the contact panel) closes too.
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
      }}
      className="w-80 rounded-xl border border-lynch-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 [&>svg]:size-4">
          {STEP_META[step.type].icon}
          {STEP_META[step.type].label}
        </span>
        <Button
          size="2xs"
          text
          aria-label="Save and close"
          tooltip="Save and close"
          icon={<General.XMark />}
          onClick={onClose}
        />
      </div>

      {issue != null && (
        <div
          data-step-issue={issue.level}
          className={cn(
            'mb-2 flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-xs leading-relaxed',
            issue.level === 'warning'
              ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
              : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300',
          )}>
          <Alerts.AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{issue.message}</span>
        </div>
      )}

      {showReplyToggle && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Continue previous email thread</span>
          <ToggleSwitch
            checked={step.replyToPrevious ?? true}
            onChange={(value) => onChange({ ...step, replyToPrevious: value })}
            size="sm"
          />
        </div>
      )}

      {threadContinued && (
        <div className="mb-2 flex items-start gap-1.5 rounded-lg bg-lynch-50 px-2.5 py-1.5 text-xs leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <Arrows.CornerUpLeft className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0">
            Replies in the previous thread — keeps its subject
            {inheritedSubjectText.length > 0 && (
              <>
                {' · '}
                <span className="font-medium text-slate-800 dark:text-slate-100">{inheritedSubjectText}</span>
              </>
            )}
          </span>
        </div>
      )}

      {spec.subject === true && !threadContinued && (
        <div className="mb-2 flex flex-col gap-1">
          <Label size="sm">Subject</Label>
          <TipTapInput
            placeholder="Subject line…"
            value={step.subject ?? ''}
            onChange={(html) => onChange({ ...step, subject: html })}
            onSubmit={onClose}
            variables={variables}
            documents={documents}
            showComments
            rows={1}
            autoFocus
          />
        </div>
      )}

      {/* A reminder picks a reusable team "reminder action" from a dropdown (parity with campaigns) — its
          name is stored on the step body and becomes the rep to-do's title. Its dropdown renders in a
          native `[popover]`, which the parent's click-outside guard ignores (see SequenceGraphEditor). */}
      {step.type === 'reminder' && !contentOnly && (
        <div className="flex flex-col gap-1">
          <Label size="sm">Action</Label>
          <ManualSelectPure
            value={step.body || undefined}
            reminderActions={reminderActions}
            isLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            isCreating={reminderCreating}
            onChange={(name) => onChange({ ...step, body: name })}
            fullWidth
          />
        </div>
      )}

      {spec.body != null && spec.body !== false && step.type !== 'reminder' && (
        <div className="flex flex-col gap-1">
          <Label size="sm">{BODY_LABEL[step.type] ?? 'Body'}</Label>
          <TipTapInput
            placeholder={BODY_PLACEHOLDER[step.type] ?? 'Write…'}
            value={step.body ?? ''}
            onChange={(html) => onChange({ ...step, body: html })}
            onSubmit={onClose}
            variables={isTaskNote ? undefined : variables}
            documents={isTaskNote ? undefined : documents}
            showComments={!isTaskNote}
            allowFormatting={rich}
            allowLinks={rich}
            rows={4}
            autoFocus={spec.subject !== true || threadContinued}
          />
        </div>
      )}

      {spec.likeCount === true && !contentOnly && (
        <div className="flex flex-col gap-1">
          <Label size="sm">Posts to like</Label>
          <Input
            type="number"
            min={1}
            max={50}
            value={likeStr}
            onChange={(e) => onLikeChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
              e.preventDefault();
              onClose();
            }}
          />
        </div>
      )}

      {/* How it saves, so nobody wonders whether it took (see SequenceGraphEditor). The builder saves as you type
          and ⌘/Ctrl+Enter saves & closes; a contact's copy saves when the card closes (⌘/Ctrl+Enter, the ✕,
          Escape or a click away). */}
      <div data-step-editor-hint className="mt-2 flex justify-end text-[11px] text-lynch-400 dark:text-lynch-500">
        <span>
          {!contentOnly && 'Saves as you type · '}
          <Kbd>{macPC('⌘', 'Ctrl')} Enter</Kbd> {contentOnly ? 'to save' : 'to save & close'}
        </span>
      </div>
    </div>
  );
}
